import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import type { Player } from '../../types';

interface SubstitutionPlan {
    position: string;
    starter: Player;
    backup: Player;
    rotation: {
      player: Player;
      situation: string;
      notes: string;
    }[];
  color?: string;
}

interface SubstitutionReport {
  formation: string;
  unit: 'offense' | 'defense';
  strategy: string;
  substitutions: SubstitutionPlan[];
}

interface SubstitutionReportProps {
  teamId?: string;
  plan?: SubstitutionReport;
  showHeader?: boolean;
}

// Convert hex codes to color family names
const hexToColorName = (hex: string): string => {
  if (!hex || !hex.startsWith('#')) return hex;
  
  // Remove # and convert to RGB
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  // Simple color detection based on dominant RGB values
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;
  
  // Handle grayscale
  if (diff < 30) {
    if (max < 85) return 'Black';
    if (max < 170) return 'Gray';
    return 'White';
  }
  
  // Handle colors
  if (max === r) {
    if (g > b && g > 100) return 'Orange';
    if (b > g && b > 100) return 'Purple';
    return 'Red';
  } else if (max === g) {
    if (r > b && r > 100) return 'Yellow';
    if (b > r && b > 100) return 'Teal';
    return 'Green';
  } else {
    if (r > g && r > 100) return 'Magenta';
    if (g > r && g > 100) return 'Cyan';
    return 'Blue';
  }
};

// Get style for color display
const getColorStyle = (color: string) => {
  if (color?.startsWith('#')) {
    // Calculate brightness to determine text color
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    return {
      backgroundColor: color,
      color: brightness > 128 ? '#000000' : '#ffffff',
      opacity: 0.9
    };
  }
  return {};
};

// Color name to hex mapping for consistent display
const colorNameToHex = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    'red': '#EF4444',
    'blue': '#3B82F6', 
    'green': '#10B981',
    'yellow': '#F59E0B',
    'purple': '#8B5CF6',
    'orange': '#F97316',
    'pink': '#EC4899',
    'teal': '#14B8A6',
    'indigo': '#6366F1',
    'cyan': '#06B6D4',
    'lime': '#84CC16',
    'emerald': '#059669',
    'violet': '#7C3AED',
    'fuchsia': '#D946EF',
    'rose': '#F43F5E',
    'sky': '#0EA5E9',
    'amber': '#F59E0B',
    'gray': '#6B7280',
    'grey': '#6B7280',
    'black': '#1F2937',
    'white': '#F9FAFB'
  };
  
  const lowerName = colorName.toLowerCase();
  return colorMap[lowerName] || colorName;
};

// Render color squares for rotation sequence
const renderColorSquares = (colorsString: string) => {
  if (!colorsString) return <div>No colors</div>;
  
  const colors = colorsString.split(',').filter(c => c.trim());
  
  console.log('Rendering color squares for:', colorsString, 'Split into:', colors);
  
  return (
    <div className="flex gap-2 items-center">
      {colors.map((color, index) => {
        const trimmedColor = color.trim();
        let displayColor = trimmedColor;
        let colorName = trimmedColor;
        
        // Convert color name to hex if needed
        if (!trimmedColor.startsWith('#')) {
          displayColor = colorNameToHex(trimmedColor);
          colorName = trimmedColor;
        } else {
          colorName = hexToColorName(trimmedColor);
        }
        
        console.log(`Color ${index}: "${trimmedColor}" -> "${displayColor}" (${colorName})`);
        console.log('Final style for color square:', getColorStyle(displayColor));
        
        return (
          <div
            key={index}
            className="w-8 h-8 rounded border-2 border-gray-400 flex items-center justify-center text-xs font-bold shadow-sm"
            style={getColorStyle(displayColor)}
            title={colorName}
          >
          </div>
        );
      })}
    </div>
  );
};

export function SubstitutionReport({ teamId, plan, showHeader = true }: SubstitutionReportProps) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // If plan is provided as prop, use it. Otherwise, get from location state
  let reportPlan = plan;
  let offensivePlan, defensivePlan, offensiveFormation, defensiveFormation, offensiveStrategy, defensiveStrategy;

  if (!reportPlan && location.state) {
    ({
    offensivePlan,
    defensivePlan,
    offensiveFormation,
    defensiveFormation,
    offensiveStrategy,
    defensiveStrategy
  } = location.state as {
    offensivePlan?: SubstitutionReport;
    defensivePlan?: SubstitutionReport;
    offensiveFormation: string;
    defensiveFormation: string;
    offensiveStrategy: string;
    defensiveStrategy: string;
    });
  }

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderUnitReport = (planData: SubstitutionReport | undefined) => {
    if (!planData) return null;

    console.log('Rendering unit report for strategy:', planData.strategy);
    console.log('All substitutions:', planData.substitutions);

    // Separate regular substitutions from rotation sequence
    const regularSubs = planData.substitutions.filter(sub => sub.position !== 'Rotation Sequence');
    const rotationSequence = planData.substitutions.find(sub => sub.position === 'Rotation Sequence');

    console.log('Regular subs:', regularSubs);
    console.log('Rotation sequence:', rotationSequence);

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {planData.formation} - {planData.unit.charAt(0).toUpperCase() + planData.unit.slice(1)}
          </h3>
          <p className="text-sm text-gray-500">
            Strategy: {planData.strategy.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </p>
        </div>

        {/* Regular Color Package Cards - Only for Color Sequence Strategy */}
        {planData.strategy === 'color_sequence' && regularSubs.length > 0 && (
          <div className="p-4 color-packages-section">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Color Packages</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regularSubs.map((substitution, index) => (
                  <div 
                    key={index}
                  className="p-4 border rounded-lg"
                  style={getColorStyle(substitution.color || '')}
                  >
                    <div className="space-y-2">
                      {substitution.rotation?.[0]?.notes.split('\n').map((player, i) => (
                      <div key={i} className="text-sm opacity-90">
                          {player}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
        )}

        {/* Color Rotation Sequence */}
            {rotationSequence && (
          <div className="p-4 border-t border-gray-200 rotation-sequence-section">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Color Rotation Sequence</h4>
                <div className="space-y-3">
              {rotationSequence.rotation.map((rotation, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                >
                  <div className="font-medium text-sm">
                        {rotation.situation}
                  </div>
                  <div className="flex items-center gap-3">
                    {renderColorSquares(rotation.notes)}
                  </div>
                    </div>
                  ))}
                </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-800">
                <strong>Strategy:</strong> Always maintain at least one starting color on the field. 
                Starting colors rotate in/out with backup colors to provide fresh packages while maintaining continuity.
              </div>
            </div>
          </div>
        )}

        {/* Segment-Based Layout for Other Strategies */}
        {!rotationSequence && regularSubs.length > 0 && (
          <div className="p-4 border-t border-gray-200 segment-based-section">
            <h4 className="text-md font-semibold text-gray-900 mb-4">
              {planData.strategy === 'package_based' ? 'Player Packages' : 'Substitution Segments'}
            </h4>
            {(() => {
              // For package-based strategy, display packages directly
              if (planData.strategy === 'package_based') {
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {regularSubs.map((substitution, index) => {
                      // Special styling for Rotation Schedule
                      if (substitution.position === 'Rotation Schedule') {
                        return (
                          <div key={index} className="md:col-span-2 bg-blue-50 rounded-lg border border-blue-200 p-4">
                            <h5 className="text-lg font-semibold mb-3 text-blue-900">
                              {substitution.rotation?.[0]?.situation || substitution.position}
                            </h5>
                            <div className="text-sm text-blue-800 space-y-2">
                              {substitution.rotation?.[0]?.notes.split('\n').map((line, i) => (
                                <div key={i} className={line.trim() === '' ? 'h-2' : ''}>
                                  {line.trim() !== '' && line}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      
                      // Regular package styling
                      return (
                        <div key={index} className="bg-gray-50 rounded-lg border p-4">
                          <h5 className="text-lg font-semibold mb-3 text-gray-900">
                            {substitution.rotation?.[0]?.situation || substitution.position}
                          </h5>
                          <div className="space-y-2">
                            {substitution.rotation?.[0]?.notes.split('\n').map((player, i) => {
                              const [position, playerName] = player.split(': ');
                              return (
                                <div key={i} className="text-sm">
                                  <div className="flex items-center">
                                    <span className="font-medium text-gray-700 w-8">{position}</span>
                                    <span className="text-gray-600">{playerName}</span>
                                  </div>
                                  {i < substitution.rotation?.[0]?.notes.split('\n').length - 1 && (
                                    <hr className="my-2 border-gray-300" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }

              // For other strategies, use the existing segment grouping logic
              const segmentMap: Record<string, { position: string; player: string }[]> = {};
              
              regularSubs.forEach((substitution) => {
                substitution.rotation?.forEach((rot) => {
                  if (!segmentMap[rot.situation]) {
                    segmentMap[rot.situation] = [];
                  }
                  segmentMap[rot.situation].push({
                    position: substitution.position,
                    player: rot.notes
                  });
                });
              });

              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(segmentMap).map(([segment, players]) => (
                    <div key={segment} className="bg-gray-50 rounded-lg border p-4">
                      <h5 className="text-lg font-semibold mb-3 text-gray-900">
                        {segment}
                      </h5>
                      <div className="space-y-2">
                        {players.map((player, index) => (
                          <div key={index} className="text-sm">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-700 w-8">{player.position}</span>
                              <span className="text-gray-600">{player.player}</span>
                            </div>
                            {index < players.length - 1 && <hr className="my-2 border-gray-300" />}
                          </div>
                        ))}
              </div>
            </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={showHeader ? "container mx-auto px-4 py-8" : "space-y-4"}>
      {/* Header - only show if showHeader is true */}
      {showHeader && (
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark"
        >
          <Printer className="w-5 h-5 mr-2" />
          Print Report
        </button>
      </div>
      )}

      {/* Report Content */}
      <div className="print:px-0">
        {reportPlan ? (
          renderUnitReport(reportPlan)
        ) : (
          <>
        {renderUnitReport(offensivePlan)}
        {renderUnitReport(defensivePlan)}
          </>
        )}
      </div>

      {/* Print Styles */}
      {showHeader && (
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print\\:px-0, .print\\:px-0 * {
              visibility: visible;
            }
            .print\\:px-0 {
              position: absolute;
              left: 0;
              top: 0;
            }
          }
        `}
      </style>
      )}
    </div>
  );
} 